import { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
// HomePage is on the critical-render path for the public root URL, so we
// import it eagerly. EVERY other route is split into its own chunk via
// React.lazy() so a casual visitor doesn't have to download the admin
// dashboard, booking flow, magazine designer, ebook reader, etc. before
// the homepage can paint. This is the single biggest TTI win after deploy.
import HomePage from "@/pages/HomePage";
const PortfolioPage = lazy(() => import("@/pages/PortfolioPage"));
const GalleryPage = lazy(() => import("@/pages/GalleryPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const CollaborationPage = lazy(() => import("@/pages/CollaborationPage"));
const MagazinePage = lazy(() => import("@/pages/MagazinePage"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const ClientDashboard = lazy(() => import("@/pages/ClientDashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const MagazineDesigner = lazy(() => import("@/pages/MagazineDesigner"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const CollaborationPolicyPage = lazy(() => import("@/pages/CollaborationPolicyPage"));
const TakedownPage = lazy(() => import("@/pages/TakedownPage"));
const BookLanding = lazy(() => import("@/pages/BookLanding"));
const CollabLanding = lazy(() => import("@/pages/CollabLanding"));
const AboutUs = lazy(() => import("@/pages/AboutUs"));
const ContactUs = lazy(() => import("@/pages/ContactUs"));
const OutreachPage = lazy(() => import("@/pages/OutreachPage"));
const OutreachAcceptFlow = lazy(() => import("@/pages/OutreachAcceptFlow"));
const CommunityPage = lazy(() => import("@/pages/CommunityPage"));
const EbookStorePage = lazy(() => import("@/pages/EbookStorePage"));
const EbookReaderPage = lazy(() => import("@/pages/EbookReaderPage"));
const CreatorAssessmentPage = lazy(() => import("@/pages/CreatorAssessmentPage"));
const CreatorToolsPage = lazy(() => import("@/pages/CreatorToolsPage"));
const LifestyleShootPage = lazy(() => import("@/pages/LifestyleShootPage"));
const CreatorLandingPage = lazy(() => import("@/pages/CreatorLandingPage"));
const RegionLandingPage = lazy(() => import("@/pages/RegionLandingPage"));
const BlogIndexPage = lazy(() => import("@/pages/BlogIndexPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
import { TenantProvider, useTenant, useTenantLoading } from "@/contexts/TenantContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";

// Suspense fallback — dark splash that matches the brand. Kept dead-simple
// (no React tree, no animations beyond CSS) so it paints the instant the
// route loader fires, then the lazy chunk takes over.
const RouteFallback = () => (
  <div
    aria-busy="true"
    role="status"
    style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#050505",
    }}
    data-testid="route-suspense-fallback"
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: "2px solid rgba(217,70,239,0.18)",
        borderTopColor: "#D946EF",
        animation: "px-spin 0.9s linear infinite",
      }}
    />
    <style>{`@keyframes px-spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// REACT_APP_BACKEND_URL points to the Render backend; falls back to same-origin for local dev
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API = `${BACKEND_URL}/api`;

/**
 * Convert a relative /api/media/... path to a full backend URL.
 * Absolute URLs (https://...) pass through unchanged.
 */
export function mediaUrl(path) {
  if (!path) return path;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${BACKEND_URL}${path}`;
}

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("propix3ls_token");
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser(res.data))
        .catch(() => localStorage.removeItem("propix3ls_token"))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch SEO settings
  useEffect(() => {
    axios.get(`${API}/seo`).then(r => setSeo(r.data)).catch(() => {});
  }, []);

  const login = (token, userData) => {
    localStorage.setItem("propix3ls_token", token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("propix3ls_token");
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#D946EF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400 font-[Manrope]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <HelmetProvider>
        <AuthContext.Provider value={{ user, setUser, login, logout, API }}>
          <BrowserRouter>
            <TenantProvider>
              <CurrencyProvider>
                <AppLayout user={user} seo={seo} />
              </CurrencyProvider>
            </TenantProvider>
          </BrowserRouter>
        </AuthContext.Provider>
      </HelmetProvider>
    </GoogleOAuthProvider>
  );
}

function AppLayout({ user, seo }) {
  const location = useLocation();
  const tenant = useTenant();
  const tenantLoading = useTenantLoading();
  const isAdmin = location.pathname.startsWith("/admin");
  const isOutreach = location.pathname.startsWith("/outreach");
  const isAdsLanding = location.pathname.startsWith("/lifestyle-shoot");
  const hideChrome = isAdmin || isOutreach || isAdsLanding || !!tenant;

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Track page views for analytics
  useEffect(() => {
    if (isAdmin) return;
    const sessionId = sessionStorage.getItem("px_sid") || (() => {
      const id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("px_sid", id);
      return id;
    })();
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    axios.post(`${API}/analytics/track`, {
      type: "page_view",
      page: location.pathname,
      query_string: location.search || "",
      referrer: document.referrer || "",
      session_id: sessionId,
      device: isMobile ? "mobile" : "desktop",
      tenant_id: tenant?.tier_id || null,
    }).catch(() => {});
  }, [location.pathname, isAdmin, tenant]);

  // While we don't yet know if this is a tenant host, avoid flashing the
  // wrong site (which would expose Propix3ls chrome on a creator's domain).
  if (tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="w-12 h-12 border-2 border-[#D946EF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ─── White-label tenant route set ──────────────────────────────────────
  if (tenant) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: '#050505' }}>
        <Helmet>
          <title>{tenant.tier_name}</title>
          {tenant.branding?.favicon_url && <link rel="icon" href={tenant.branding.favicon_url} />}
        </Helmet>
        <main className="flex-1">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<CreatorLandingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/community" element={user ? <CommunityPage /> : <Navigate to="/auth" />} />
              <Route path="/dashboard" element={user ? <ClientDashboard /> : <Navigate to="/auth" />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              {/* Anything else on a creator domain → back to their landing */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
        <Toaster position="bottom-right" theme="dark" />
      </div>
    );
  }

  // ─── Default Propix3ls site ─────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#050505' }}>
      {seo && (
        <Helmet>
          {/* Sitewide verification tags + non-gtag analytics. The Google tag
              (gtag.js) is hardcoded in `public/index.html` so Google's
              verifier detects it on first byte and we strictly comply with
              Google's "one tag per page" rule. */}
          {seo.google_site_verification && <meta name="google-site-verification" content={seo.google_site_verification} />}
          {seo.bing_site_verification && <meta name="msvalidate.01" content={seo.bing_site_verification} />}
          {seo.gtm_id && (
            <script>{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${seo.gtm_id}');`}</script>
          )}
        </Helmet>
      )}
      <div className="grain-overlay" />
      {!hideChrome && <Navbar />}
      <main className="flex-1">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/portfolio/:category" element={<GalleryPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/collaborate" element={<CollaborationPage />} />
            <Route path="/magazine" element={<MagazinePage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/dashboard" element={user ? <ClientDashboard /> : <Navigate to="/auth" />} />
            <Route path="/admin" element={user?.role === "admin" || user?.is_staff ? <AdminDashboard /> : <Navigate to="/" />} />
            <Route path="/admin/magazine-editor" element={user?.role === "admin" || user?.is_staff ? <MagazineDesigner /> : <Navigate to="/" />} />
            <Route path="/admin/magazine-editor/:id" element={user?.role === "admin" || user?.is_staff ? <MagazineDesigner /> : <Navigate to="/" />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/collaboration-policy" element={<CollaborationPolicyPage />} />
            <Route path="/takedown" element={<TakedownPage />} />
            <Route path="/book" element={<BookLanding />} />
            <Route path="/collab" element={<CollabLanding />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/outreach/:slug" element={<OutreachPage />} />
            <Route path="/outreach/:slug/accept" element={<OutreachAcceptFlow />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/ebooks" element={<EbookStorePage />} />
            <Route path="/ebook/:id" element={<EbookReaderPage />} />
            <Route path="/creator-assessment" element={<CreatorAssessmentPage />} />
            <Route path="/creator-tools" element={<CreatorToolsPage />} />
            <Route path="/lifestyle-shoot" element={<LifestyleShootPage />} />
            {/* SEO region landing pages */}
            <Route path="/lifestyle-photographer-london" element={<RegionLandingPage region="london" />} />
            <Route path="/ugc-content-creator-usa" element={<RegionLandingPage region="usa" />} />
            <Route path="/lifestyle-content-canada" element={<RegionLandingPage region="canada" />} />
            {/* Blog */}
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
          </Routes>
        </Suspense>
      </main>
      {!hideChrome && <Footer />}
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default App;
